import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, AlertTriangle, Sparkles } from 'lucide-react';

/**
 * Trial banner displayed at the top of the clinic dashboard for trial clinics.
 * Shows days remaining, or an expired/blocking state with CTA to convert.
 */
export const TrialBanner: React.FC = () => {
  const { isTrial, trialDaysRemaining, trialExpired } = useClinic();
  const navigate = useNavigate();

  if (!isTrial) return null;

  if (trialExpired) {
    return (
      <div className="mb-4 rounded-xl border border-red-500/30 bg-[#22201E] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div>
              <p className="font-semibold text-[#F5F0EA]">Your trial has expired</p>
              <p className="text-sm text-red-300">
                Complete onboarding to continue using the platform with full access.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/onboard/clinic?mode=convert')}
            className="bg-red-600 hover:bg-red-700 text-white font-['Montserrat'] font-medium rounded-full px-6"
            size="sm"
          >
            Complete Onboarding
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-[#F5F0EA]/10 bg-[#22201E] p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-[#2A9D8F]" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-[#F5F0EA]">Free Trial</p>
              <Badge variant="outline" className="border-[#2A9D8F] text-[#2A9D8F] bg-transparent">
                <Clock className="mr-1 h-3 w-3" />
                {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
              </Badge>
            </div>
            <p className="text-sm text-[#F5F0EA]/50 font-['Inter']">
              Explore the platform with sample data. Complete onboarding anytime to unlock full access.
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/onboard/clinic?mode=convert')}
          className="bg-[#B87A5D] hover:bg-[#A56A4D] text-white font-['Montserrat'] font-medium rounded-full px-6"
          size="sm"
        >
          Complete Onboarding
        </Button>
      </div>
    </div>
  );
};
